/**
 * The command bar's filter language: what `campaign:"Energy Premium"
 * status:sold value:>5 sam` means, and the search body it becomes.
 *
 * Pure functions only, so every rule is testable without a modal. The
 * component passes in the lists it has cached (campaigns, suppliers, buyers,
 * source tags) and today's date in London, and gets back chips and a body for
 * `POST /api/v1/leads/search`. Nothing here talks to the server.
 *
 * Grammar, loosely:
 *
 *   query   = either*                      every one must match (AND)
 *   either  = primary (OR primary)*        any one may match
 *   primary = term | ( query )
 *   term    = [-]key:value | free text
 *   key     = a name from KEYS (any case), or custom.<field>
 *   value   = word | "quoted words"
 *
 * OR (or `or`) joins the terms either side of it, so it binds tighter than
 * the spaces between terms: `status:sold OR status:rejected date:today` is
 * either status, today. Brackets group more than one term:
 * `(campaign:a status:sold) OR value:>10`.
 *
 * Forgiving where it can be:
 *
 * - Keys ignore case, and each has aliases (`stream:` is `campaign:`,
 *   `partner:` is `buyer:`, `is:` is `status:`).
 * - A name may run over several unquoted words while they still spell the
 *   start of a known name, so `from:energy premium status:sold` reads
 *   "Energy Premium" without quotes.
 * - A name matches exactly, then by a unique prefix, then by a unique word
 *   start, then by a unique substring.
 *
 * And strict where silence would mislead: an unknown key, a value that names
 * nothing, or a filter that cannot be negated becomes an error chip and is
 * left out of the search, rather than quietly widening it.
 *
 * `repeats:>1` keeps only rows whose person has sent more than one (the
 * server's `repeats_min`, at least two). It applies to the whole search,
 * even written beside an OR, and is offered only to someone who may see
 * people (`QueryContext.people`).
 *
 * `owner:me`, `owner:none` and `owner:"Priya Shah"` keep rows whose person
 * is owned by the one asking, by nobody, or by that member (the server's
 * `owner`). Also for people only, and only while owners are in use
 * (`QueryContext.ownership`). A name matches the organisation's members as
 * any other name does; it cannot be excluded.
 *
 * `score:>50`, `score:20..50` and `band:hot` keep rows by the score worked
 * out when the lead arrived (the server's `min_score`, `max_score` and
 * `score_band`). Scores are whole numbers, so more than 50 is 51 or more.
 * Scores are internal: offered only where the organisation scores and to
 * someone who may see them (`QueryContext.scoring`). A band matches the
 * organisation's band names as any other name does. Neither can be
 * excluded.
 *
 * Keys mean the same thing in every organisation. `source:` (and `tag:`) is
 * always the source tag on the record, and `supplier:` is always the data
 * seller: a query that changed meaning with the wording could not be shared
 * or saved safely. The help panel spells this out.
 */
import type { LeadFilters } from '../../api/queries';

export type KeyId =
  | 'campaign' | 'supplier' | 'buyer' | 'from' | 'status' | 'date' | 'after' | 'before'
  | 'value' | 'source' | 'has' | 'repeats' | 'owner' | 'score' | 'band' | PersonKey | 'custom';

/** Keys that search one person field on the record, rather than all of them. */
export type PersonKey = 'first' | 'last' | 'name' | 'email' | 'phone' | 'company';

/** Every spelling each key accepts, lower case. The first is the canonical one. */
export const KEY_NAMES: Record<Exclude<KeyId, 'custom'>, string[]> = {
  campaign: ['campaign', 'stream'],
  supplier: ['supplier'],
  buyer: ['buyer', 'partner'],
  from: ['from'],
  status: ['status', 'is'],
  date: ['date', 'on'],
  after: ['after', 'since'],
  before: ['before', 'until'],
  value: ['value'],
  source: ['source', 'tag'],
  has: ['has'],
  repeats: ['repeats', 'repeat'],
  owner: ['owner', 'assigned'],
  score: ['score'],
  band: ['band'],
  first: ['first', 'firstname'],
  last: ['last', 'lastname', 'surname'],
  name: ['name'],
  email: ['email'],
  phone: ['phone'],
  company: ['company'],
};

/**
 * The column each person key searches, and what a chip calls it. Free text
 * searches all of these at once; a person key searches just the one, so
 * `first:sam` finds Sam Jones and not Pat Samuels.
 */
export const PERSON_FIELDS: Record<PersonKey, { column: string; label: string }> = {
  first: { column: 'firstname', label: 'First name' },
  last: { column: 'lastname', label: 'Last name' },
  name: { column: 'fullname', label: 'Name' },
  email: { column: 'email', label: 'Email' },
  phone: { column: 'phone1', label: 'Phone' },
  company: { column: 'company', label: 'Company' },
};

export function isPersonKey(id: KeyId | null): id is PersonKey {
  return id !== null && id in PERSON_FIELDS;
}

/** The person key for a column, under any of the names the server accepts for it. */
export function personKeyForColumn(field: string): PersonKey | null {
  const f = field.toLowerCase();
  const aliases: Record<string, string> = {
    first_name: 'firstname', last_name: 'lastname', full_name: 'fullname', phone: 'phone1',
  };
  const column = aliases[f] ?? f;
  for (const [id, p] of Object.entries(PERSON_FIELDS)) {
    if (p.column === column) return id as PersonKey;
  }
  return null;
}

/** Custom fields are `custom.<field>:value`. */
export const CUSTOM_PREFIX = 'custom.';

/** The rule the server enforces on a field name before it reaches SQL. */
export const FIELD_NAME = /^[A-Za-z0-9_]+$/;

export function keyIdFor(name: string): KeyId | null {
  const n = name.toLowerCase();
  if (n.startsWith(CUSTOM_PREFIX)) return 'custom';
  for (const [id, names] of Object.entries(KEY_NAMES)) {
    if (names.includes(n)) return id as KeyId;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Statuses
// ---------------------------------------------------------------------------

/**
 * Status words and the field each one filters. The delivery statuses are the
 * real values of `leads.delivery_status`; sold and unsold are the `sold` flag,
 * through the server's `sold_statuses`.
 */
export const STATUSES: Record<string, { field: 'delivery' | 'sold'; value: string }> = {
  delivered: { field: 'delivery', value: 'delivered' },
  sold: { field: 'sold', value: 'sold' },
  unsold: { field: 'sold', value: 'unsold' },
  rejected: { field: 'delivery', value: 'rejected' },
  pending: { field: 'delivery', value: 'pending' },
  filtered: { field: 'delivery', value: 'filtered' },
  duplicate: { field: 'delivery', value: 'duplicate' },
  error: { field: 'delivery', value: 'error' },
  suppressed: { field: 'delivery', value: 'suppressed' },
  returned: { field: 'delivery', value: 'returned' },
};

/** Other spellings people use, "accepted" among them. */
const STATUS_ALIASES: Record<string, string> = {
  accepted: 'sold',
  notsold: 'unsold',
  not_sold: 'unsold',
  unaccepted: 'unsold',
  not_accepted: 'unsold',
  dupe: 'duplicate',
  duplicates: 'duplicate',
  failed: 'error',
  errored: 'error',
};

export function statusFor(value: string): string | null {
  const v = value.toLowerCase();
  if (STATUSES[v]) return v;
  return STATUS_ALIASES[v] ?? null;
}

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

/** An inclusive span of London calendar days, as `YYYY-MM-DD`. */
export type DaySpan = { from: string; to: string };

/** Today's date in London, as `YYYY-MM-DD`: filter boundaries are London days. */
export function londonToday(now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

function toDate(iso: string): Date {
  const [y = 1970, m = 1, d = 1] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

function iso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function addDays(day: string, n: number): string {
  const d = toDate(day);
  d.setUTCDate(d.getUTCDate() + n);
  return iso(d);
}

function validDay(y: number, m: number, d: number): string | null {
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null;
  return iso(dt);
}

function monthSpan(y: number, m: number): DaySpan {
  const from = `${y}-${String(m).padStart(2, '0')}-01`;
  const next = m === 12 ? `${y + 1}-01-01` : `${y}-${String(m + 1).padStart(2, '0')}-01`;
  return { from, to: addDays(next, -1) };
}

const MONTHS = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
];

/** The month a word names: "oct", "sept", "October". At least three letters. */
function monthOf(word: string): number | null {
  const w = word.toLowerCase();
  if (w.length < 3) return null;
  const i = MONTHS.findIndex((m) => m.startsWith(w));
  return i === -1 ? null : i + 1;
}

/**
 * The words `date:` understands, each with the server's own preset where one
 * exists, so "today" is resolved in London on the server exactly as the
 * leads page resolves it.
 */
export const DATE_PRESETS: { word: string; preset: string | null; aliases: string[] }[] = [
  { word: 'today', preset: 'today', aliases: [] },
  { word: 'yesterday', preset: 'yesterday', aliases: [] },
  { word: 'last7', preset: '7d', aliases: ['7d', 'last7days', 'last_7_days', 'week'] },
  { word: 'last30', preset: '30d', aliases: ['30d', 'last30days', 'last_30_days'] },
  { word: 'last90', preset: '90d', aliases: ['90d', 'last90days', 'last_90_days'] },
  { word: 'thisweek', preset: null, aliases: ['this_week'] },
  { word: 'thismonth', preset: 'this_month', aliases: ['this_month', 'month'] },
  { word: 'lastmonth', preset: 'last_month', aliases: ['last_month'] },
];

export type DateValue = { span: DaySpan; preset: string | null };

/** One date word or day, as the span of days it covers. */
function singleDate(value: string, today: string): DateValue | null {
  const v = value.toLowerCase().trim();
  const preset = DATE_PRESETS.find((p) => p.word === v || p.aliases.includes(v));
  if (preset) {
    const t = toDate(today);
    switch (preset.word) {
      case 'today': return { span: { from: today, to: today }, preset: preset.preset };
      case 'yesterday': {
        const y = addDays(today, -1);
        return { span: { from: y, to: y }, preset: preset.preset };
      }
      case 'last7': return { span: { from: addDays(today, -6), to: today }, preset: preset.preset };
      case 'last30': return { span: { from: addDays(today, -29), to: today }, preset: preset.preset };
      case 'last90': return { span: { from: addDays(today, -89), to: today }, preset: preset.preset };
      case 'thisweek': {
        // Weeks start on Monday in the UK.
        const back = (t.getUTCDay() + 6) % 7;
        return { span: { from: addDays(today, -back), to: today }, preset: null };
      }
      case 'thismonth': {
        return { span: { from: `${today.slice(0, 8)}01`, to: today }, preset: preset.preset };
      }
      case 'lastmonth': {
        const last = addDays(`${today.slice(0, 8)}01`, -1);
        return { span: { from: `${last.slice(0, 8)}01`, to: last }, preset: preset.preset };
      }
    }
  }

  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(v);
  if (m) {
    const d = validDay(Number(m[1]), Number(m[2]), Number(m[3]));
    return d ? { span: { from: d, to: d }, preset: null } : null;
  }
  // UK order, as people write it: 01/10/2026.
  m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(v);
  if (m) {
    const d = validDay(Number(m[3]), Number(m[2]), Number(m[1]));
    return d ? { span: { from: d, to: d }, preset: null } : null;
  }
  m = /^(\d{4})-(\d{1,2})$/.exec(v);
  if (m) {
    const month = Number(m[2]);
    if (month < 1 || month > 12) return null;
    return { span: monthSpan(Number(m[1]), month), preset: null };
  }
  // A month on its own is the most recent one that has started: in October,
  // "nov" is last November.
  const month = /^[a-z]+$/.test(v) ? monthOf(v) : null;
  if (month) {
    const year = Number(today.slice(0, 4));
    const current = Number(today.slice(5, 7));
    return { span: monthSpan(month > current ? year - 1 : year, month), preset: null };
  }
  return null;
}

/** A date word, a day, a month, or a `start..end` range of any of those. */
export function parseDate(value: string, today: string): DateValue | null {
  const range = value.split('..');
  if (range.length === 2) {
    const a = singleDate(range[0] ?? '', today);
    const b = singleDate(range[1] ?? '', today);
    if (!a || !b || a.span.from > b.span.to) return null;
    return { span: { from: a.span.from, to: b.span.to }, preset: null };
  }
  return singleDate(value, today);
}

export function isDateLike(value: string, today: string): boolean {
  return parseDate(value, today) !== null;
}

// ---------------------------------------------------------------------------
// Numbers
// ---------------------------------------------------------------------------

export type Comparison = { op: '>' | '<' | '>=' | '<=' | '='; n: number };

export function parseComparison(value: string): Comparison | null {
  const m = /^(>=|<=|>|<|=)?\s*£?(-?\d+(?:\.\d+)?)$/.exec(value.trim());
  if (!m) return null;
  return { op: (m[1] as Comparison['op'] | undefined) ?? '=', n: Number(m[2]) };
}

/** `5..20`, both ends inclusive. */
export function parseNumberRange(value: string): { min: number; max: number } | null {
  const m = /^£?(\d+(?:\.\d+)?)\.\.£?(\d+(?:\.\d+)?)$/.exec(value.trim());
  if (!m) return null;
  const min = Number(m[1]);
  const max = Number(m[2]);
  return min <= max ? { min, max } : null;
}

// ---------------------------------------------------------------------------
// Names
// ---------------------------------------------------------------------------

export type Named = { id: string; name: string };

/**
 * What the parser needs to know. A list is null when this person cannot see
 * it (or it has not loaded), which is different from an empty list: a name
 * that cannot be checked is matched by name on the server where the server
 * can do that, and is an error only where it cannot.
 */
export type QueryContext = {
  campaigns: Named[] | null;
  suppliers: Named[] | null;
  buyers: Named[] | null;
  sources: string[] | null;
  /** London `YYYY-MM-DD`. */
  today: string;
  /**
   * Whether this person may see people, and so use `repeats:`. Left out, it
   * is allowed; false makes the key unknown, as for a scoped login.
   */
  people?: boolean;
  /**
   * Whether owners are in use here (the CRM area), and so `owner:`. Left
   * out, it is allowed; false makes the key unknown. It also needs `people`.
   */
  ownership?: boolean;
  /** Members who can own a record, for `owner:"Name"`. Null: not loaded. */
  owners?: Named[] | null;
  /**
   * Whether scores are offered here, and so `score:` and `band:`: the
   * organisation scores and this person may see scores. Left out, they are
   * allowed; false makes both keys unknown, as for a scoped login.
   */
  scoring?: boolean;
  /** The score bands, by key and name, for `band:hot`. Null: not loaded. */
  bands?: Named[] | null;
};

type Resolved = { match: Named } | { error: 'none' | 'many' } | { unknown: true };

const lower = (s: string) => s.toLowerCase();

/** Exact, then a unique prefix, then a unique word start, then a unique substring. */
export function resolveName(value: string, list: Named[] | null): Resolved {
  if (list === null) return { unknown: true };
  const q = lower(value.trim());
  if (!q) return { error: 'none' };
  const exact = list.filter((n) => lower(n.name) === q);
  const first = exact[0];
  if (first) return { match: first };
  const tiers = [
    (n: Named) => lower(n.name).startsWith(q),
    (n: Named) => lower(n.name).split(/[\s\-_/]+/).some((w) => w.startsWith(q)),
    (n: Named) => lower(n.name).includes(q),
  ];
  for (const test of tiers) {
    const hits = list.filter(test);
    const only = hits[0];
    if (hits.length === 1 && only) return { match: only };
    if (hits.length > 1) return { error: 'many' };
  }
  return { error: 'none' };
}

/** Wrap a value in quotes when it would not survive as one word. */
export function quote(value: string): string {
  return /[\s"]/.test(value) || value === '' ? `"${value.replace(/"/g, '')}"` : value;
}

// ---------------------------------------------------------------------------
// Tokens
// ---------------------------------------------------------------------------

export type Token = {
  start: number;
  end: number;
  /** The token as typed. */
  raw: string;
  neg: boolean;
  /** The key as typed, or null for free text. */
  key: string | null;
  /** The value without its quotes. */
  value: string;
  quoted: boolean;
  /** OR, or a bracket. Such a token has no key and is never free text. */
  op?: Op;
};

export type Op = 'or' | '(' | ')';

/**
 * A key is a letter and then anything up to the colon, so a malformed one
 * (`custom.x');--:1`) is still read as a key and shown as an error, rather
 * than slipping into the free text unnoticed. A time such as 10:30 starts
 * with a digit and stays free text.
 */
const KEY_TOKEN = /^(-?)([A-Za-z][^\s:"]*):([\s\S]*)$/;

function unquote(s: string): { value: string; quoted: boolean } {
  if (s.startsWith('"')) {
    const inner = s.slice(1);
    return { value: (inner.endsWith('"') ? inner.slice(0, -1) : inner), quoted: true };
  }
  return { value: s, quoted: false };
}

/** Split on spaces outside quotes. An unclosed quote runs to the end. */
export function tokenize(input: string): Token[] {
  const out: Token[] = [];
  let i = 0;
  while (i < input.length) {
    while (i < input.length && /\s/.test(input[i] ?? '')) i++;
    if (i >= input.length) break;
    const start = i;
    let inQuote = false;
    while (i < input.length && (inQuote || !/\s/.test(input[i] ?? ''))) {
      if (input[i] === '"') inQuote = !inQuote;
      i++;
    }
    pushWord(out, input.slice(start, i), start);
  }
  return out;
}

const opToken = (op: Op, start: number, raw: string): Token =>
  ({ start, end: start + raw.length, raw, neg: false, key: null, value: raw, quoted: false, op });

/**
 * One word as tokens: brackets at its start and end (outside quotes) are
 * tokens of their own, and so is a bare OR, so `(status:sold` is a bracket
 * and a filter.
 */
function pushWord(out: Token[], word: string, at: number) {
  let s = 0;
  let e = word.length;
  while (s < e && word[s] === '(') {
    out.push(opToken('(', at + s, '('));
    s++;
  }
  // A closing bracket counts only outside quotes: `"A (B)"` keeps its own.
  const closers: Token[] = [];
  while (e > s && word[e - 1] === ')' && quotesBalanced(word.slice(s, e - 1))) {
    e--;
    closers.unshift(opToken(')', at + e, ')'));
  }
  const raw = word.slice(s, e);
  const start = at + s;
  if (raw) {
    if (/^or$/i.test(raw)) {
      out.push(opToken('or', start, raw));
    } else {
      const m = KEY_TOKEN.exec(raw);
      if (m) {
        const { value, quoted } = unquote(m[3] ?? '');
        out.push({ start, end: at + e, raw, neg: m[1] === '-', key: m[2] ?? '', value, quoted });
      } else {
        const { value, quoted } = unquote(raw);
        out.push({ start, end: at + e, raw, neg: false, key: null, value, quoted });
      }
    }
  }
  out.push(...closers);
}

function quotesBalanced(s: string): boolean {
  return (s.match(/"/g) ?? []).length % 2 === 0;
}

/** True when there is an OR or a bracket among the tokens. */
export function hasOperators(tokens: Token[]): boolean {
  return tokens.some((t) => t.op);
}

/** The names a key's value is matched against, for running over spaces. */
function candidates(id: KeyId | null, ctx: QueryContext): string[] {
  switch (id) {
    case 'campaign': return (ctx.campaigns ?? []).map((n) => n.name);
    case 'supplier': return (ctx.suppliers ?? []).map((n) => n.name);
    case 'buyer': return (ctx.buyers ?? []).map((n) => n.name);
    case 'from': return [...(ctx.campaigns ?? []), ...(ctx.suppliers ?? [])].map((n) => n.name);
    case 'owner': return (ctx.owners ?? []).map((n) => n.name);
    case 'band': return (ctx.bands ?? []).map((n) => n.name);
    default: return [];
  }
}

/** True when some name starts with this value and carries on past a space. */
export function canExtend(id: KeyId | null, value: string, ctx: QueryContext): boolean {
  const prefix = `${lower(value)} `;
  return candidates(id, ctx).some((n) => lower(n).startsWith(prefix));
}

/**
 * Join an unquoted name to the plain words after it while together they
 * still spell the start of a known name.
 */
export function mergeNames(tokens: Token[], ctx: QueryContext): Token[] {
  const out: Token[] = [];
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i] as Token;
    const id = t.key ? keyIdFor(t.key) : null;
    if (!t.key || t.quoted || !t.value || !['campaign', 'supplier', 'buyer', 'from', 'owner', 'band'].includes(id ?? '')) {
      out.push(t);
      continue;
    }
    let merged = { ...t };
    const names = candidates(id, ctx).map(lower);
    while (i + 1 < tokens.length) {
      const next = tokens[i + 1] as Token;
      if (next.key || next.quoted || next.op) break;
      const joined = `${merged.value} ${next.value}`;
      if (!names.some((n) => n.startsWith(lower(joined)))) break;
      merged = { ...merged, value: joined, end: next.end, raw: `${merged.raw} ${next.raw}` };
      i++;
    }
    out.push(merged);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Chips
// ---------------------------------------------------------------------------

export type FieldOperator =
  | 'equals' | 'not_equals' | 'contains' | 'not_contains'
  | 'greater_than' | 'less_than' | 'greater_equal' | 'less_equal'
  | 'is_empty' | 'is_not_empty'
  | 'in' | 'not_in' | 'starts_with';

export type Filter =
  | { kind: 'campaign'; id: string; name: string }
  | { kind: 'supplier'; id: string | null; name: string }
  | { kind: 'buyer'; id: string | null; name: string }
  | { kind: 'status'; status: string }
  | { kind: 'date'; span: DaySpan; preset: string | null }
  | { kind: 'after'; day: string }
  | { kind: 'before'; day: string }
  | { kind: 'value'; min: number | null; max: number | null }
  | { kind: 'source'; value: string }
  | { kind: 'repeats'; min: number }
  /** `me`, `none` or a member's id, as the server's `owner` takes it. */
  | { kind: 'owner'; owner: string; name: string }
  /** Whole-number bounds on the score, either end open. */
  | { kind: 'score'; min: number | null; max: number | null }
  /** A band's key (or the name typed, when the bands are not loaded). */
  | { kind: 'band'; band: string; name: string }
  | { kind: 'field'; field: string; operator: FieldOperator; value: string };

export type ChipError =
  | { code: 'unknown_key'; key: string }
  | { code: 'empty' }
  | { code: 'no_match'; noun: 'campaign' | 'supplier' | 'buyer' | 'from' | 'owner' | 'band' }
  | { code: 'many_match'; noun: 'campaign' | 'supplier' | 'buyer' | 'from' | 'owner' | 'band' }
  | { code: 'bad_status' }
  | { code: 'bad_date' }
  | { code: 'bad_number' }
  | { code: 'bad_repeats' }
  | { code: 'bad_score' }
  | { code: 'bad_field' }
  | { code: 'no_negation' };

export type Chip = {
  /** The key as typed, which is what the chip is written back as. */
  keyText: string;
  key: KeyId | null;
  neg: boolean;
  value: string;
  filter: Filter | null;
  error: ChipError | null;
  start: number;
  end: number;
};

/** A key token as a chip: what it filters, or why it cannot. */
export function chipFor(t: Token, ctx: QueryContext): Chip {
  const keyText = t.key ?? '';
  const id = keyIdFor(keyText);
  const base = { keyText, key: id, neg: t.neg, value: t.value, start: t.start, end: t.end };
  const fail = (error: ChipError): Chip => ({ ...base, filter: null, error });
  const ok = (filter: Filter): Chip => ({ ...base, filter, error: null });

  if (!id) return fail({ code: 'unknown_key', key: keyText });
  const value = t.value.trim();
  if (!value && id !== 'custom') return fail({ code: 'empty' });

  switch (id) {
    case 'campaign': {
      const r = resolveName(value, ctx.campaigns);
      if ('match' in r) return ok({ kind: 'campaign', id: r.match.id, name: r.match.name });
      // There is no campaign name on the record to match on instead.
      return fail({ code: 'error' in r && r.error === 'many' ? 'many_match' : 'no_match', noun: 'campaign' });
    }
    case 'supplier':
    case 'buyer': {
      const r = resolveName(value, id === 'supplier' ? ctx.suppliers : ctx.buyers);
      if ('match' in r) return ok({ kind: id, id: r.match.id, name: r.match.name });
      // A list this person cannot see: match the name on the record instead.
      if ('unknown' in r) return ok({ kind: id, id: null, name: value });
      return fail({ code: r.error === 'many' ? 'many_match' : 'no_match', noun: id });
    }
    case 'from': {
      const date = parseDate(value, ctx.today);
      if (date) {
        if (t.neg) return fail({ code: 'no_negation' });
        return ok({ kind: 'after', day: date.span.from });
      }
      // An exact name wins, a campaign first. Otherwise the value must pick
      // out one name across both lists: "en" matching two campaigns is not
      // quietly read as the one supplier it also matches.
      const exact = (list: Named[] | null) => list?.find((n) => lower(n.name) === lower(value));
      const ec = exact(ctx.campaigns);
      if (ec) return ok({ kind: 'campaign', id: ec.id, name: ec.name });
      const es = exact(ctx.suppliers);
      if (es) return ok({ kind: 'supplier', id: es.id, name: es.name });
      const c = resolveName(value, ctx.campaigns);
      const s = resolveName(value, ctx.suppliers);
      const many = (r: Resolved) => 'error' in r && r.error === 'many';
      if ('match' in c && !('match' in s) && !many(s)) return ok({ kind: 'campaign', id: c.match.id, name: c.match.name });
      if ('match' in s && !('match' in c) && !many(c)) return ok({ kind: 'supplier', id: s.match.id, name: s.match.name });
      if ('unknown' in s && !('match' in c) && !many(c)) return ok({ kind: 'supplier', id: null, name: value });
      const ambiguous = 'match' in c || 'match' in s || many(c) || many(s);
      return fail({ code: ambiguous ? 'many_match' : 'no_match', noun: 'from' });
    }
    case 'status': {
      const s = statusFor(value);
      return s ? ok({ kind: 'status', status: s }) : fail({ code: 'bad_status' });
    }
    case 'date':
    case 'after':
    case 'before': {
      if (t.neg) return fail({ code: 'no_negation' });
      const d = parseDate(value, ctx.today);
      if (!d) return fail({ code: 'bad_date' });
      if (id === 'date') return ok({ kind: 'date', span: d.span, preset: d.preset });
      return ok(id === 'after' ? { kind: 'after', day: d.span.from } : { kind: 'before', day: d.span.to });
    }
    case 'value': {
      if (t.neg) return fail({ code: 'no_negation' });
      const range = parseNumberRange(value);
      if (range) return ok({ kind: 'value', min: range.min, max: range.max });
      const c = parseComparison(value);
      if (!c) return fail({ code: 'bad_number' });
      // Values are pence-precise, so "more than £5" is £5.01 or more.
      switch (c.op) {
        case '>': return ok({ kind: 'value', min: round2(c.n + 0.01), max: null });
        case '>=': return ok({ kind: 'value', min: c.n, max: null });
        case '<': return ok({ kind: 'value', min: null, max: round2(c.n - 0.01) });
        case '<=': return ok({ kind: 'value', min: null, max: c.n });
        case '=': return ok({ kind: 'value', min: c.n, max: c.n });
      }
      break;
    }
    case 'source': {
      const known = (ctx.sources ?? []).find((s) => lower(s) === lower(value));
      return ok({ kind: 'source', value: known ?? value });
    }
    case 'repeats': {
      // People are not for every login: to anyone else the key does not exist.
      if (ctx.people === false) return fail({ code: 'unknown_key', key: keyText });
      if (t.neg) return fail({ code: 'no_negation' });
      const min = repeatsMin(value);
      return min === null ? fail({ code: 'bad_repeats' }) : ok({ kind: 'repeats', min });
    }
    case 'owner': {
      // Owners are people's, and part of the CRM: to anyone else the key does not exist.
      if (ctx.people === false || ctx.ownership === false) return fail({ code: 'unknown_key', key: keyText });
      if (t.neg) return fail({ code: 'no_negation' });
      const o = ownerOf(value, ctx);
      return 'error' in o ? fail({ code: o.error, noun: 'owner' }) : ok({ kind: 'owner', owner: o.owner, name: o.name });
    }
    case 'score': {
      // Scores are internal: to anyone who may not see them the key does not exist.
      if (ctx.scoring === false) return fail({ code: 'unknown_key', key: keyText });
      if (t.neg) return fail({ code: 'no_negation' });
      const s = scoreBounds(value);
      return s ? ok({ kind: 'score', min: s.min, max: s.max }) : fail({ code: 'bad_score' });
    }
    case 'band': {
      if (ctx.scoring === false) return fail({ code: 'unknown_key', key: keyText });
      if (t.neg) return fail({ code: 'no_negation' });
      const r = resolveName(value, ctx.bands ?? null);
      if ('match' in r) return ok({ kind: 'band', band: r.match.id, name: r.match.name });
      // Bands not loaded: the server matches the name, and an unknown one finds nothing.
      if ('unknown' in r) return ok({ kind: 'band', band: value, name: value });
      return fail({ code: r.error === 'many' ? 'many_match' : 'no_match', noun: 'band' });
    }
    case 'has': {
      if (!FIELD_NAME.test(value)) return fail({ code: 'bad_field' });
      return ok({ kind: 'field', field: value, operator: t.neg ? 'is_empty' : 'is_not_empty', value: '' });
    }
    case 'custom': {
      const field = keyText.slice(CUSTOM_PREFIX.length);
      if (!FIELD_NAME.test(field)) return fail({ code: 'bad_field' });
      if (!value) return fail({ code: 'empty' });
      const f = customFilter(field, value);
      const op = t.neg ? NEGATED[f.operator] : f.operator;
      return op ? ok({ ...f, operator: op }) : fail({ code: 'no_negation' });
    }
    default: {
      if (!isPersonKey(id)) break;
      const f = personFilter(id, value);
      if (!f) return fail({ code: 'empty' });
      const op = t.neg ? NEGATED[f.operator] : f.operator;
      return op ? ok({ ...f, operator: op }) : fail({ code: 'no_negation' });
    }
  }
  return fail({ code: 'unknown_key', key: keyText });
}

/**
 * Whole-number score bounds from `>50`, `>=51`, `<20`, `<=19`, `=45`, `45`
 * or `20..50`. Scores are whole, so more than 50 is at least 51. Null for
 * anything else, a fraction included.
 */
export function scoreBounds(value: string): { min: number | null; max: number | null } | null {
  const v = value.trim();
  const range = /^(-?\d+)\s*\.\.\s*(-?\d+)$/.exec(v);
  if (range) {
    const a = Number(range[1]);
    const b = Number(range[2]);
    return a <= b ? { min: a, max: b } : null;
  }
  const m = /^(>=|<=|>|<|=)?\s*(-?\d+)$/.exec(v);
  if (!m) return null;
  const n = Number(m[2]);
  switch (m[1]) {
    case '>': return { min: n + 1, max: null };
    case '>=': return { min: n, max: null };
    case '<': return { min: null, max: n - 1 };
    case '<=': return { min: null, max: n };
    default: return { min: n, max: n };
  }
}

/** Words for "mine" and "nobody's" that `owner:` takes. */
export const OWNER_ME = ['me', 'mine', 'my'];
export const OWNER_NONE = ['none', 'nobody', 'unassigned', 'unowned'];

/** An owner value: me, nobody, or one member by name. */
export function ownerOf(
  value: string, ctx: QueryContext,
): { owner: string; name: string } | { error: 'no_match' | 'many_match' } {
  const v = lower(value.trim());
  if (OWNER_ME.includes(v)) return { owner: 'me', name: 'me' };
  if (OWNER_NONE.includes(v)) return { owner: 'none', name: 'nobody' };
  const r = resolveName(value, ctx.owners ?? null);
  if ('match' in r) return { owner: r.match.id, name: r.match.name };
  return { error: 'error' in r && r.error === 'many' ? 'many_match' : 'no_match' };
}

/**
 * The fewest submissions a person must have, from `>1`, `>=2` or `2`.
 * Counts are whole, so more than one is at least two. Only a lower bound
 * means anything here.
 */
export function repeatsMin(value: string): number | null {
  const m = /^(>=|>|=)?\s*(\d+)$/.exec(value.trim());
  if (!m) return null;
  const n = Number(m[2]);
  const min = m[1] === '>' ? n + 1 : n;
  return min >= 1 && min <= 100000 ? min : null;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Each operator's opposite. Starts with has none on the server, so it cannot be negated. */
const NEGATED: Partial<Record<FieldOperator, FieldOperator>> = {
  equals: 'not_equals',
  not_equals: 'equals',
  contains: 'not_contains',
  not_contains: 'contains',
  greater_than: 'less_equal',
  less_equal: 'greater_than',
  less_than: 'greater_equal',
  greater_equal: 'less_than',
  is_empty: 'is_not_empty',
  is_not_empty: 'is_empty',
  in: 'not_in',
  not_in: 'in',
};

/**
 * A person field value: `=sam` is exactly sam, `sam*` starts with sam, and
 * otherwise sam anywhere in the field. All ignore case, so `first:=sam`
 * finds the Sam the record stores. Null when nothing is left to search for.
 *
 * Exactly is sent as `in`, which the server compares in lower case;
 * `equals` would miss Sam. A value with a comma in it would be split by
 * `in` into a list, so that one is sent as `equals` and matches its case.
 * Phone numbers are stored without spaces, so spaces typed are dropped.
 */
function personFilter(id: PersonKey, value: string): Extract<Filter, { kind: 'field' }> | null {
  const field = PERSON_FIELDS[id].column;
  let v = value.trim();
  let operator: FieldOperator = 'contains';
  if (v.startsWith('=')) {
    // `name:="Sam Smith"` arrives with its quotes still on.
    v = v.slice(1).trim().replace(/^"([^"]*)"?$/, '$1').trim();
    operator = v.includes(',') ? 'equals' : 'in';
  } else if (v.endsWith('*')) {
    v = v.replace(/\*+$/, '').trim();
    operator = 'starts_with';
  }
  if (id === 'phone') v = v.replace(/\s+/g, '');
  if (!v) return null;
  return { kind: 'field', field, operator, value: v };
}

/**
 * A custom field value: yes or no, a number or comparison, `=exact`, or
 * otherwise text matched anywhere in the field, ignoring case.
 */
function customFilter(field: string, value: string): Extract<Filter, { kind: 'field' }> {
  const v = value.trim();
  const l = lower(v);
  if (['true', 'yes'].includes(l)) return { kind: 'field', field, operator: 'equals', value: 'true' };
  if (['false', 'no'].includes(l)) return { kind: 'field', field, operator: 'equals', value: 'false' };
  const c = /^(>=|<=|>|<)\s*(-?\d+(?:\.\d+)?)$/.exec(v);
  if (c) {
    const ops: Record<string, FieldOperator> = { '>': 'greater_than', '<': 'less_than', '>=': 'greater_equal', '<=': 'less_equal' };
    return { kind: 'field', field, operator: ops[c[1] ?? '>'] ?? 'greater_than', value: c[2] ?? '' };
  }
  if (v.startsWith('=')) return { kind: 'field', field, operator: 'equals', value: v.slice(1) };
  if (/^-?\d+(?:\.\d+)?$/.test(v)) return { kind: 'field', field, operator: 'equals', value: v };
  return { kind: 'field', field, operator: 'contains', value: v };
}

/**
 * A query as a tree. Leaves are chips and free text; `and` needs every item,
 * `or` any one.
 */
export type Expr =
  | { op: 'and'; items: Expr[] }
  | { op: 'or'; items: Expr[] }
  | { op: 'chip'; chip: Chip }
  | { op: 'text'; text: string };

export type Parsed = {
  chips: Chip[];
  /** Everything that is not a filter, joined by single spaces. */
  text: string;
  /** The whole query as a tree, OR and brackets included. */
  expr: Expr;
  /** True when an OR in the query joins two things. */
  hasOr: boolean;
};

/**
 * Parse a query given as parts: the bar's finished terms, one per part, and
 * then what is still in the box. Names run over spaces only within a part, so
 * a finished `supplier:acme` never swallows the next word typed.
 */
export function parseParts(parts: string[], ctx: QueryContext): Parsed {
  const chips: Chip[] = [];
  const words: string[] = [];
  // Leaves and operators, in order, for the tree.
  const items: ({ leaf: Expr } | { op: Op })[] = [];
  for (const part of parts) {
    for (const t of mergeNames(tokenize(part), ctx)) {
      if (t.op) {
        items.push({ op: t.op });
      } else if (t.key) {
        const chip = chipFor(t, ctx);
        chips.push(chip);
        items.push({ leaf: { op: 'chip', chip } });
      } else if (/^-?custom\./i.test(t.raw)) {
        // A custom field name still being typed is a filter on its way, not
        // a name to search for.
        continue;
      } else if (t.value.trim()) {
        words.push(t.value.trim());
        items.push({ leaf: { op: 'text', text: t.value.trim() } });
      }
    }
  }
  const expr = buildExpr(items);
  return { chips, text: words.join(' '), expr, hasOr: containsOr(expr) };
}

export function parseQuery(input: string, ctx: QueryContext): Parsed {
  return parseParts([input], ctx);
}

/**
 * The tree for a run of leaves and operators. Forgiving, like the rest: an
 * OR with nothing on one side is ignored, a closing bracket with no opening
 * one is ignored, and an opening bracket still open at the end is closed
 * there, so a query being typed always has a meaning.
 */
function buildExpr(items: ({ leaf: Expr } | { op: Op })[]): Expr {
  let i = 0;
  const seq = (inner: boolean): Expr => {
    const out: Expr[] = [];
    while (i < items.length) {
      const it = items[i] as { leaf: Expr } | { op: Op };
      if ('op' in it && it.op === ')') {
        i++;
        if (inner) break;
        continue;
      }
      const first = primary();
      if (!first) continue;
      const alts = [first];
      for (;;) {
        const next = items[i];
        if (!next || !('op' in next) || next.op !== 'or') break;
        i++;
        // Skip ORs in a row; an OR before a closing bracket or the end has
        // nothing on its right and is dropped.
        while (items[i] && 'op' in (items[i] as object) && (items[i] as { op: Op }).op === 'or') i++;
        const after = items[i];
        if (!after || ('op' in after && after.op === ')')) break;
        const p = primary();
        if (p) alts.push(p);
      }
      out.push(alts.length === 1 ? (alts[0] as Expr) : { op: 'or', items: alts });
    }
    return out.length === 1 ? (out[0] as Expr) : { op: 'and', items: out };
  };
  // One leaf or bracketed group; null for a stray OR, which is skipped.
  const primary = (): Expr | null => {
    const it = items[i] as { leaf: Expr } | { op: Op };
    i++;
    if ('leaf' in it) return it.leaf;
    if (it.op === '(') return seq(true);
    return null;
  };
  return seq(false);
}

function containsOr(e: Expr): boolean {
  if (e.op === 'or') return e.items.filter((x) => !isEmptyExpr(x)).length > 1 || e.items.some(containsOr);
  if (e.op === 'and') return e.items.some(containsOr);
  return false;
}

function isEmptyExpr(e: Expr): boolean {
  return (e.op === 'and' || e.op === 'or') && e.items.every(isEmptyExpr);
}

/** The most OR groups one search may send, as the server allows. */
export const MAX_OR_GROUPS = 16;

/** Every way the tree can be met, each a list of leaves; null past the limit. */
export function eitherWays(e: Expr): Expr[][] | null {
  switch (e.op) {
    case 'chip':
    case 'text':
      return [[e]];
    case 'or': {
      const out: Expr[][] = [];
      for (const item of e.items) {
        const ways = eitherWays(item);
        if (!ways) return null;
        out.push(...ways.filter((w) => w.length > 0));
        if (out.length > MAX_OR_GROUPS) return null;
      }
      return out.length ? out : [[]];
    }
    case 'and': {
      let out: Expr[][] = [[]];
      for (const item of e.items) {
        const ways = eitherWays(item);
        if (!ways) return null;
        const next: Expr[][] = [];
        for (const a of out) for (const b of ways) next.push([...a, ...b]);
        if (next.length > MAX_OR_GROUPS) return null;
        out = next;
      }
      return out;
    }
  }
}

function leafBody(leaves: Expr[]): LeadFilters {
  const chips = leaves.flatMap((l) => (l.op === 'chip' ? [l.chip] : []));
  const text = leaves.flatMap((l) => (l.op === 'text' ? [l.text] : [])).join(' ');
  return toSearchBody(chips, text);
}

const hasError = (leaves: Expr[]) => leaves.some((l) => l.op === 'chip' && l.chip.error !== null);

/**
 * Each way an OR query can be met, as a whole search body of its own, or
 * null when the query has no OR (or too many ways to send).
 *
 * A way with a chip in error is left out, as a chip in error is left out of
 * an AND: it never widens the search to everything. So is one with nothing
 * to search on yet, such as a single letter being typed.
 */
export function toOrGroups(p: Parsed): LeadFilters[] | null {
  if (!p.hasOr) return null;
  const ways = eitherWays(p.expr);
  if (!ways) return null;
  const groups = ways
    .filter((w) => !hasError(w))
    .map(leafBody)
    .filter((b) => !isEmptyBody(b));
  return groups;
}

/** Why an OR query cannot be searched as typed, or null. */
export function orProblem(p: Parsed): 'too_many' | null {
  return p.hasOr && eitherWays(p.expr) === null ? 'too_many' : null;
}

/**
 * The search body for a parsed query, OR and all.
 *
 * Without OR, exactly `toSearchBody`. With it, what every way shares goes in
 * the body itself and the rest in `any_of`, one group per way, so the server
 * ANDs the shared part with any one group. A way left out (see toOrGroups)
 * leaves the others; with one way left it is a plain search.
 */
export function toQueryBody(p: Parsed): LeadFilters {
  if (!p.hasOr) return toSearchBody(p.chips, p.text);
  return liftRepeats(orQueryBody(p), p.chips);
}

/**
 * `repeats_min` applies to the whole search on the server, never inside an
 * OR group, so wherever it was written it goes on the body itself.
 */
function liftRepeats(body: LeadFilters, chips: Chip[]): LeadFilters {
  const mins = chips.flatMap((c) => (c.filter?.kind === 'repeats' && !c.error ? [c.filter.min] : []));
  if (mins.length === 0) return body;
  const groups = (body.any_of as LeadFilters[] | undefined)
    ?.map((g) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { repeats_min, ...rest } = g;
      return rest;
    })
    .filter((g) => !isEmptyBody(g));
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { any_of, ...base } = body;
  const out: LeadFilters = { ...base, repeats_min: Math.max(...mins) };
  return groups && groups.length > 1 ? ({ ...out, any_of: groups } as LeadFilters) : groups?.length === 1 ? { ...out, ...groups[0] } : out;
}

function orQueryBody(p: Parsed): LeadFilters {
  const ways = eitherWays(p.expr);
  if (!ways) return {};
  // Leaves in every way: those ANDed outside the OR.
  const first = ways[0] ?? [];
  const common = first.filter((l) => ways.every((w) => w.includes(l)));
  const kept = ways.filter((w) => !hasError(w.filter((l) => !common.includes(l))));
  const rests = kept
    .map((w) => w.filter((l) => !common.includes(l)))
    .filter((r) => r.length > 0 && !isEmptyBody(leafBody(r)));
  const base = leafBody(common.filter((l) => !(l.op === 'chip' && l.chip.error)));
  // A way with nothing beyond the shared part is always met: no OR needed.
  if (kept.some((w) => w.every((l) => common.includes(l)))) return base;
  if (rests.length === 0) return base;
  if (rests.length === 1) return leafBody([...common, ...(rests[0] as Expr[])].filter((l) => !(l.op === 'chip' && l.chip.error)));
  return { ...base, any_of: rests.map(leafBody) } as LeadFilters;
}

/** Terms back into one line, brackets hugging what they hold: `(a OR b)`. */
export function joinTerms(parts: string[]): string {
  return parts.join(' ').replace(/\(\s+/g, '(').replace(/\s+\)/g, ')').replace(/\s+/g, ' ').trim();
}

/** Words typed, all free text, before plain English is offered. */
export const INTERPRET_MIN_WORDS = 3;

/**
 * Whether what is typed looks like plain English worth turning into
 * filters: three words or more, and none of them a filter, OR or bracket.
 */
export function wantsInterpreting(text: string, ctx: QueryContext): boolean {
  const tokens = tokenize(text);
  if (tokens.length < INTERPRET_MIN_WORDS) return false;
  if (hasOperators(tokens) || tokens.some((t) => t.key)) return false;
  return parseQuery(text, ctx).chips.length === 0;
}

/**
 * Plain English, back from the server as a query, into the bar: filters
 * become finished terms, and free text or a filter that names nothing stays
 * as text, listed in `kept` for a note. The parts the AI could not place are
 * listed in `dropped` and left out of the search altogether. With OR in it, a query that has free text
 * or a filter in error is left whole in the box, since splitting it would
 * change what it means.
 */
export function splitInterpreted(
  query: string, unknown: string[], ctx: QueryContext,
): { terms: string[]; text: string; kept: string[]; dropped: string[] } {
  const tokens = mergeNames(tokenize(query), ctx);
  const dropped = unknown.map((u) => u.trim()).filter(Boolean);
  const kept: string[] = [];
  if (hasOperators(tokens) && tokens.some((t) => !t.op && (!t.key || chipFor(t, ctx).error))) {
    return { terms: [], text: query.trim(), kept, dropped };
  }
  const terms: string[] = [];
  const words: string[] = [];
  for (const t of tokens) {
    if (t.op) {
      terms.push(t.op === 'or' ? 'OR' : t.raw);
    } else if (t.key && !chipFor(t, ctx).error) {
      terms.push(serializeChip({ keyText: t.key, neg: t.neg, value: t.value }));
    } else if (t.value.trim()) {
      words.push(t.value.trim());
      if (t.key) kept.push(t.value.trim());
    }
  }
  // Words the AI could not use are reported, not searched: as free text they
  // would match names, so "best" found people with "best" in their name.
  return { terms: tidyTerms(terms), text: words.join(' '), kept, dropped };
}

/**
 * Finished terms with any OR or bracket that no longer makes sense taken
 * out: after a chip is removed, an OR with nothing on one side, a bracket
 * with nothing inside, a closing bracket with no opening one.
 */
export function tidyTerms(terms: string[]): string[] {
  const opOf = (t: string): Op | null => {
    const tok = tokenize(t);
    return tok.length === 1 && tok[0]?.op ? tok[0].op : null;
  };
  let out = [...terms];
  for (let changed = true; changed;) {
    changed = false;
    const next: string[] = [];
    let depth = 0;
    for (let i = 0; i < out.length; i++) {
      const t = out[i] as string;
      const op = opOf(t);
      const prev = next.length ? opOf(next[next.length - 1] as string) : 'start';
      const after = i + 1 < out.length ? opOf(out[i + 1] as string) : 'end';
      if (op === 'or' && (prev === 'start' || prev === 'or' || prev === '(' || after === ')' || after === 'end' || after === 'or')) {
        changed = true;
        continue;
      }
      if (op === '(' && after === ')') {
        changed = true;
        i++;
        continue;
      }
      if (op === ')') {
        if (depth === 0) { changed = true; continue; }
        depth--;
      }
      if (op === '(') depth++;
      next.push(t);
    }
    out = next;
  }
  return out;
}

/** A chip written back as a term, so a parsed query can be typed out again. */
export function serializeChip(chip: Pick<Chip, 'keyText' | 'neg' | 'value'>): string {
  return `${chip.neg ? '-' : ''}${chip.keyText}:${quote(chip.value)}`;
}

/**
 * Whether a typed term is finished, so the bar can turn it into a chip.
 *
 * A name that could still run on to a longer known name is not finished
 * ("energy" while "Energy Premium" exists), nor is a key with no value yet.
 * Errors are finished: they become a red chip rather than lingering as text.
 */
export function isSettled(t: Token, ctx: QueryContext): boolean {
  if (!t.key) return false;
  if (!t.value.trim()) return false;
  if (t.quoted) return t.raw.endsWith('"') && t.raw.length > t.key.length + (t.neg ? 3 : 2);
  const id = keyIdFor(t.key);
  if (id === 'from' && isDateLike(t.value, ctx.today)) return true;
  return !canExtend(id, t.value, ctx);
}

/**
 * Move every finished term out of the text, as written-back terms for chips,
 * keeping free text where it was.
 */
export function commitTerms(
  text: string, ctx: QueryContext, ordered = false,
): { terms: string[]; rest: string } {
  const tokens = mergeNames(tokenize(text), ctx);
  const terms: string[] = [];
  let rest = '';
  let last = 0;
  // With OR or brackets about, where a term sits changes what it means, so
  // only a leading run of finished terms and operators moves, in order. The
  // first free text or unfinished term stops it, and the rest stays as typed.
  if (ordered || hasOperators(tokens)) {
    for (const t of tokens) {
      if (!t.op && !isSettled(t, ctx)) break;
      terms.push(t.op ? (t.op === 'or' ? 'OR' : t.raw) : serializeChip({ keyText: t.key ?? '', neg: t.neg, value: t.value }));
      last = t.end;
      if (text[last] === ' ') last++;
    }
    return { terms, rest: text.slice(last).replace(/^\s+/, '') };
  }
  for (const t of tokens) {
    if (isSettled(t, ctx)) {
      terms.push(serializeChip({ keyText: t.key ?? '', neg: t.neg, value: t.value }));
      rest += text.slice(last, t.start);
      last = t.end;
      // Drop one space after the term too, so the text does not fill with gaps.
      if (text[last] === ' ') last++;
    }
  }
  rest += text.slice(last);
  return { terms, rest: rest.replace(/^\s+/, '') };
}

// ---------------------------------------------------------------------------
// Search body
// ---------------------------------------------------------------------------

/** Fewest characters of free text worth searching on. */
export const MIN_FREE_TEXT = 2;

/**
 * The search body for a set of chips and free text.
 *
 * Every positive filter uses the field the leads page uses, so "Open in
 * Leads" shows the same rows in its own panel. A negated one becomes a
 * `field_filters` condition, which the server applies to the column directly.
 * Chips in error are left out, so they never widen or narrow anything.
 */
export function toSearchBody(chips: Chip[], text: string): LeadFilters {
  const body: LeadFilters = {};
  const fields: NonNullable<LeadFilters['field_filters']> = [];
  const push = <T>(list: T[] | null | undefined, item: T): T[] => [...(list ?? []), item];
  const sources: string[] = [];

  for (const chip of chips) {
    const f = chip.filter;
    if (!f || chip.error) continue;
    const neg = chip.neg;
    switch (f.kind) {
      case 'campaign':
        if (neg) fields.push({ field: 'campaign_id', operator: 'not_equals', value: f.id });
        else body.campaign_ids = push(body.campaign_ids, f.id);
        break;
      case 'supplier':
        if (f.id) {
          if (neg) fields.push({ field: 'supplier_id', operator: 'not_equals', value: f.id });
          else body.supplier_ids = push(body.supplier_ids, f.id);
        } else {
          fields.push({ field: 'supplier_name', operator: neg ? 'not_contains' : 'contains', value: f.name });
        }
        break;
      case 'buyer':
        if (f.id) {
          if (neg) fields.push({ field: 'buyer_id', operator: 'not_equals', value: f.id });
          else body.buyer_ids = push(body.buyer_ids, f.id);
        } else {
          fields.push({ field: 'buyer_name', operator: neg ? 'not_contains' : 'contains', value: f.name });
        }
        break;
      case 'status': {
        const s = STATUSES[f.status];
        if (!s) break;
        if (s.field === 'sold') {
          // Not sold is unsold or returned; not unsold is sold or returned.
          const tokens = neg ? (s.value === 'sold' ? ['unsold', 'returned'] : ['sold', 'returned']) : [s.value];
          for (const tok of tokens) body.sold_statuses = push(body.sold_statuses, tok);
        } else if (neg) {
          fields.push({ field: 'delivery_status', operator: 'not_equals', value: s.value });
        } else {
          body.delivery_statuses = push(body.delivery_statuses, s.value);
        }
        break;
      }
      case 'date':
        if (f.preset) {
          body.date_range = f.preset;
          body.date_from = null;
          body.date_to = null;
        } else {
          body.date_range = null;
          body.date_from = f.span.from;
          body.date_to = f.span.to;
        }
        break;
      case 'after':
        body.date_from = f.day;
        break;
      case 'before':
        body.date_to = f.day;
        break;
      case 'value':
        if (f.min !== null) body.min_value = body.min_value != null ? Math.max(body.min_value, f.min) : f.min;
        if (f.max !== null) body.max_value = body.max_value != null ? Math.min(body.max_value, f.max) : f.max;
        break;
      case 'source':
        if (neg) fields.push({ field: 'source', operator: 'not_equals', value: f.value });
        else sources.push(f.value);
        break;
      case 'repeats':
        body.repeats_min = body.repeats_min != null ? Math.max(body.repeats_min, f.min) : f.min;
        break;
      case 'owner':
        // One owner per search: the last one written wins.
        body.owner = f.owner;
        break;
      case 'score':
        // Two score terms narrow each other, as two value terms do.
        if (f.min !== null) body.min_score = body.min_score != null ? Math.max(body.min_score, f.min) : f.min;
        if (f.max !== null) body.max_score = body.max_score != null ? Math.min(body.max_score, f.max) : f.max;
        break;
      case 'band':
        // One band per search: the last one written wins.
        body.score_band = f.band;
        break;
      case 'field':
        fields.push({ field: f.field, operator: f.operator, value: f.value });
        break;
    }
  }

  // One source is the leads page's own filter; several are any of them.
  if (sources.length === 1) body.source = sources[0];
  else if (sources.length > 1) fields.push({ field: 'source', operator: 'in', value: sources.join(',') });
  if (fields.length) body.field_filters = fields;

  const q = text.trim();
  if (q.length >= MIN_FREE_TEXT) body.search = q;

  // Drop the nulls a date chip set, so an empty search really is empty.
  for (const k of Object.keys(body) as (keyof LeadFilters)[]) {
    if (body[k] === null || body[k] === undefined) delete body[k];
  }
  return body;
}

export function isEmptyBody(body: LeadFilters): boolean {
  return Object.keys(body).length === 0;
}
